import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkdownComponent, MarkdownService } from 'ngx-markdown';
import { PDFDocument } from 'src/app/core/interfaces/pdf-document.interface';

@Component({
  selector: 'app-markdown-renderer',
  standalone: true,
  providers: [MarkdownService],
  imports: [
    CommonModule,
    MarkdownComponent
  ],
  template: `
    <div class="markdown-container">
      <markdown [data]="convertText()"></markdown>
    </div>
  `,
  styles: [`
    .markdown-container {
      display: block;
      width: 100%;
    }
    
    .markdown-container ::ng-deep h1 {
      font-size: 2.25rem;
      font-weight: bold;
      margin-top: 1rem !important;
      margin-bottom: 1rem !important;
      color: #102E52;
    }
    
    .markdown-container ::ng-deep h2 {
      font-size: 1.625rem;
      font-weight: bold;
      margin-top: 1rem !important;
      margin-bottom: 0.75rem !important;
      color: #102E52;
    }
    
    .markdown-container ::ng-deep h3 {
      font-size: 1.25rem;
      font-weight: bold;
      margin-top: 0.75rem !important;
      margin-bottom: 0.5rem !important;
    }
    
    .markdown-container ::ng-deep p {
      margin-bottom: 1rem;
      line-height: 1.5;
    }
    
    .markdown-container ::ng-deep ul, 
    .markdown-container ::ng-deep ol {
      margin-left: 1.5rem;
      margin-bottom: 1rem;
    }
    
    .markdown-container ::ng-deep li {
      margin-bottom: 0.25rem;
    }
    
    .markdown-container ::ng-deep blockquote {
      border-left: 4px solid #DEE4EA;
      padding-left: 1rem;
      margin-left: 0;
      margin-right: 0;
      font-style: italic;
    }
    
    .markdown-container ::ng-deep code {
      background-color: #DEE4EA;
      padding: 0.2rem 0.4rem;
      border-radius: 3px;
      font-family: monospace;
    }
    
    .markdown-container ::ng-deep pre {
      background-color: #DEE4EA;
      padding: 1rem;
      border-radius: 3px;
      overflow-x: auto;
      margin-bottom: 1rem;
    }
    
    .markdown-container ::ng-deep pre code {
      background-color: transparent;
      padding: 0;
    }
    
    .markdown-container ::ng-deep table {
      border-collapse: collapse;
      width: 100%;
      margin-bottom: 1rem;
    }
    
    .markdown-container ::ng-deep th, 
    .markdown-container ::ng-deep td {
      border: 1px solid #DEE4EA;
      padding: 0.5rem;
    }
    
    .markdown-container ::ng-deep th {
      background-color: #F5F7FA;
      font-weight: bold;
    }
  `]
})
export class MarkdownRendererComponent {
  @Input() text: string = '';

  convertText(): string {
    return MarkdownRendererComponent.convertToMarkdown(this.text);
  }

  static convertToMarkdown(text: string): string {
    if (typeof text !== 'string' || !text) return '';
    
    // Split into lines and process each line
    return text.split('\n').map(line => {
      // Convert bold text to headers
      if (line.startsWith('**') && line.endsWith('**')) {
        return '### ' + line.replace(/\*\*/g, '');
      }
      return line;
    }).join('\n\n');
  }

  static renderToPdf(doc: PDFDocument, markdown: string, marginLeft: number, width: number): void {
    if (!markdown) return;

    const convertedMarkdown = MarkdownRendererComponent.convertToMarkdown(markdown);
    const lines = convertedMarkdown.split('\n\n');

    for (const line of lines) {
      if (line.startsWith('### ')) {
        // Header 3 (previously bold text)
        doc.font('Helvetica-Bold')
           .fontSize(12)
           .text(line.replace('### ', ''), marginLeft, doc.y, {
             width,
             continued: false
           });
        doc.moveDown(0.5);
      } else if (line.startsWith('## ')) {
        // Header 2
        doc.font('Helvetica-Bold')
           .fontSize(14)
           .text(line.replace('## ', ''), marginLeft, doc.y, {
             width,
             continued: false
           });
        doc.moveDown(0.5);
      } else if (line.startsWith('# ')) {
        // Header 1
        doc.font('Helvetica-Bold')
           .fontSize(16)
           .text(line.replace('# ', ''), marginLeft, doc.y, {
             width,
             continued: false
           });
        doc.moveDown(0.5);
      } else if (line.match(/^[-*+]\s/)) {
        // Bullet points
        doc.font('Helvetica')
           .fontSize(10)
           .text(line.replace(/^[-*+]\s/, '• '), marginLeft + 10, doc.y, {
             width: width - 10,
             continued: false
           });
        doc.moveDown(0.5);
      } else {
        // Regular text
        doc.font('Helvetica')
           .fontSize(10)
           .text(line, marginLeft, doc.y, {
             width,
             align: 'left',
             continued: false
           });
        doc.moveDown(0.5);
      }
    }
  }
} 